<template>
  <div class="ec-line013" v-loading="loading">
    <the-heading class="lg:mt-4 lg:mb-8 mt-1 mb-5">{{
      id ? $t('ec-line013.updateHeading') : $t('ec-line013.addHeading')
    }}</the-heading>

    <el-form ref="form" :model="form" :rules="rules" label-position="top">
      <the-section :title="$t('ec-line013.form.paymentInformation')">
        <template #subTitle>
          <span class="text-base lg:text-xs">{{ $t('common.requiredItem') }}</span>
        </template>
        <el-form-item v-if="isEdit" :label="$t('ec-line013.form.label.cardNumber')" required>
          <div class="font-bold">{{ Utils.helper.formatCard(form.card_number) }}</div>
        </el-form-item>
        <el-form-item v-else :label="$t('ec-line013.form.label.cardNumber')" prop="card_number">
          <el-input
            inputmode="numeric"
            v-model="form.card_number"
            :placeholder="$t('ec-line013.form.placeholder.cardNumber')"
            :maxlength="19"
          ></el-input>
        </el-form-item>
        <el-form-item :label="$t('ec-line013.form.label.dateOfExpiry')" required>
          <div class="flex w-full lg:w-1/2 space-x-2">
            <div class="flex-initial w-1/2 inline-flex items-center">
              <el-form-item prop="expiry_month">
                <el-input
                  inputmode="numeric"
                  v-model="form.expiry_month"
                  :placeholder="$t('ec-line013.form.placeholder.expiryMonth')"
                  :maxlength="2"
                ></el-input>
              </el-form-item>
              <div class="ml-2 text-cemter">{{ $t('common.dateSeparator') }}</div>
            </div>
            <div class="flex-initial w-1/2">
              <el-form-item prop="expiry_year">
                <el-input
                  inputmode="numeric"
                  v-model="form.expiry_year"
                  :placeholder="$t('ec-line013.form.placeholder.expiryYear')"
                  :maxlength="2"
                ></el-input>
              </el-form-item>
            </div>
          </div>
        </el-form-item>
        <el-form-item :label="$t('ec-line013.form.label.cardHolder')" prop="card_holder">
          <el-input
            v-model="form.card_holder"
            :placeholder="$t('ec-line013.form.placeholder.cardHolder')"
            :maxlength="50"
          ></el-input>
        </el-form-item>
        <el-form-item v-if="!isEdit" :label="$t('ec-line013.form.label.securityCode')" prop="security_code">
          <el-input
            inputmode="numeric"
            v-model="form.security_code"
            :placeholder="$t('common.pleaseEnter')"
            :maxlength="4"
            type="text"
          ></el-input>
        </el-form-item>
        <el-form-item v-if="!isEdit">
          <img :src="Constants.SHOP_CONFIG.BANK_CARD_URL" alt="card" />
        </el-form-item>
        <el-form-item :label="$t('ec-line013.form.label.numberOfPayment')" prop="number_of_payment">
          <el-select v-model="form.number_of_payment">
            <el-option v-for="item in items" :key="item.id" :value="item.id" :label="item.text"></el-option>
          </el-select>
        </el-form-item>
      </the-section>
      <div class="flex flex-col items-center lg:flex-row justify-center mt-5 lg:mt-10 lg:mb-4">
        <el-button class="submit-btn lg:ml-6 lg:order-1" @click="onSubmit">
          {{ id ? $t('common.action.change') : $t('common.action.add') }}
        </el-button>
        <el-button class="cancel-btn mt-5 mb-1 lg:my-0" @click="onCancel">{{ $t('common.action.cancel') }}</el-button>
      </div>
      <div div class="flex flex-col items-center lg:pb-4 pb-1 lg:flex-row justify-center" v-if="id">
        <el-button class="delete-btn" @click="onDelete">{{ $t('ec-line013.form.deletePayment') }}</el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import TheSection from '@/components/my-page-partials/TheSection.vue'
import TheHeading from '@/components/my-page-partials/TheHeading.vue'

import { NUMBER_OF_PAYMENT } from '@/mocks/ec-line013'
import { trimForm } from '@/utils'
import { AuthService, MyPageService, InquiryService } from '@/services'
import { cloneDeep } from 'lodash'
import GMOMixin from '@/mixins/gmo'
import { INQUIRY_STATUS_ID, INQUIRY_TYPE_ID, CARD_DEFAULT_FLAG } from '@/constants'

const defaultForm = {
  card_number: null,
  expiry_month: null,
  expiry_year: null,
  card_holder: '',
  security_code: '',
  number_of_payment: 1,
  card_seq: undefined,
}

const target_type_ids = [INQUIRY_TYPE_ID.EMAIL, INQUIRY_TYPE_ID.LINE]

const target_status_ids = [INQUIRY_STATUS_ID.DONE_CORRESPOND]

export default {
  components: { TheSection, TheHeading },
  mixins: [GMOMixin],

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    id: {
      type: [String, Number],
      default: () => null,
    },
  },

  data() {
    return {
      loading: false,
      form: Object.assign({ ...defaultForm }),
      cardInfoDefault: {},
      items: NUMBER_OF_PAYMENT,
      default_flag: CARD_DEFAULT_FLAG.NON_DEFAULT,
      rules: {
        card_number: [
          this.Utils.validate.required(this.$t('common.validate.field.cardNumber')),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.cardNumber')),
        ],

        security_code: [
          this.Utils.validate.required(this.$t('ec-line013.form.label.securityCode')),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.securityCode')),
        ],

        expiry_month: [
          this.Utils.validate.required(this.$t('common.validate.field.dateOfExpiredMonth')),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.dateOfExpiredMonth')),
          this.Utils.validate.numberBetween(this.$t('common.validate.field.dateOfExpiredMonth'), 1, 12),
        ],

        expiry_year: [
          this.Utils.validate.required(this.$t('common.validate.field.dateOfExpiredYear')),
          this.Utils.validate.numberFormat(this.$t('common.validate.field.dateOfExpiredYear')),
          this.Utils.validate.numberBetween(
            this.$t('common.validate.field.dateOfExpiredYear'),
            new Date().getFullYear().toString().slice(-2),
            (new Date().getFullYear() + 10).toString().slice(-2)
          ),
        ],
      },
    }
  },
  computed: {
    isEdit() {
      return !!this.id
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      if (this.id) {
        const { data } = await MyPageService.getCardList({ customer_id: this.user.id, card_seq: this.id })

        if (data) {
          const cardList = data.reverse()
          const [, expiry_year, expiry_month] = cardList[0].expire.split(/(\d{2})(\d{2})/)
          this.form.card_holder = cardList[0].holder_name
          this.form.expiry_year = expiry_year
          this.form.expiry_month = expiry_month
          this.form.card_seq = cardList[0].card_seq
          this.form.card_number = cardList[0].card_no
          this.default_flag = cardList[0].default_flag

          this.cardInfoDefault = cloneDeep(this.form)
        }
      }
    },
    checkValidateCardNumber() {
      this.$refs.form.validateField('card_number')
    },
    onSubmit() {
      this.form = trimForm(this.form)

      this.$nextTick(() => {
        this.$refs.form.validate((valid) => {
          if (!valid) {
            this.handleError({ message: this.$t('common.messages.hasInvalidData') })
            return
          }

          this.onSave()
        })
      })
    },
    async onSave() {
      try {
        this.loading = true

        if (this.id) {
          const card_info = {
            holder_name: this.cardInfoDefault.card_holder,
            expire: `${this.cardInfoDefault.expiry_month.padStart(2, '0')}/${this.cardInfoDefault.expiry_year.padStart(
              2,
              0
            )}`,
            default_flag: this.default_flag,
          }
          await MyPageService.updateCard({
            gmo_id: this.user.gmo_id,
            card_seq: this.form.card_seq,
            expire: `${this.form.expiry_year.padStart(2, 0)}${this.form.expiry_month.padStart(2, '0')}`,
            holder_name: this.form.card_holder,
            card_info,
          })

          this.onSubmitSuccess(this.form.card_seq)
        } else {
          const token = await this.getTokenCard({
            holdername: this.form.card_holder,
            cardno: this.form.card_number,
            expire: `20${this.form.expiry_year.padStart(2, 0)}${this.form.expiry_month.padStart(2, '0')}`,
            securitycode: this.form.security_code,
          })

          const card_info = {
            holder_name: this.form.card_holder,
            expire: `${this.form.expiry_month.padStart(2, '0')}/${this.form.expiry_year.padStart(2, 0)}`,
          }

          if (token?.length) {
            const { data } = await MyPageService.newCard({
              token: token[0],
              gmo_id: this.user.gmo_id,
              card_seq: this.form.card_seq,
              card_info,
              customer_id: this.user.id,
            })

            this.onSubmitSuccess(data ? data.CardSeq : '')
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },

    onSubmitSuccess(cardSeq = '') {
      this.updateInquiryStatus()

      if (this.id) {
        this.onEmit('submit-edit', cardSeq)
      } else {
        this.onEmit('submit-new', cardSeq)
      }
      this.onCancel(cardSeq)
    },

    onCancel(cardSeq = '') {
      this.$emit('cancel', cardSeq)
    },

    onEmit(emit = 'submit', cardSeq = '') {
      this.form = Object.assign({ ...defaultForm })
      this.$emit(emit, cardSeq)
    },

    async onDelete() {
      try {
        this.loading = true

        const card_info = {
          expire: `${this.form.expiry_month.padStart(2, '0')}/${this.form.expiry_year.padStart(2, 0)}`,
          holder_name: this.form.card_holder,
        }

        await AuthService.deleteCard({ customer_id: this.user.id, card_seq: this.id, card_info }).then(() =>
          this.$alert({ message: this.$t('ec-line013.content.deleteCardSuccess'), type: 'success' })
        )

        this.onCancel()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },
    async updateInquiryStatus() {
      const hasDiffInfo = Object.keys(this.cardInfoDefault).filter((key) => {
        return this.cardInfoDefault[key] !== this.form[key]
      })

      if (hasDiffInfo.length || !this.id) {
        const body = {
          status_id: this.Constants.INQUIRY_STATUS_ID.DURING_CORRESPOND,
          target_status_ids: target_status_ids,
          target_type_ids: target_type_ids,
          customer_id: this.user.id,
          customer_flag: true,
        }

        InquiryService.updateCustomerInquiryStatus(body)
      }
    },
  },
}
</script>
